// The flow's navigation rules, with no DOM. `<tento-onboarding-flow>` is a thin
// view over this; keeping the rules here is what lets them be deno-tested.
//
// The rules are Lona's onboarding behaviour made the library's contract:
// Continue is ignored unless the current step can proceed, and is Finish on
// the last active step; Back does nothing on the first; a context change
// (resize across the mobile breakpoint) keeps the current step if it is still
// active, otherwise returns to the step last shown on that side without
// re-entering it, otherwise starts fresh.

import { FlowModel, type FlowLabels, type Presentation, type StepNode } from "./flow-model";
import { type FlowContext, resolveSteps } from "./gating";
import type { StepKindRegistry } from "./registry";
import type { FlowNavigator, OnboardingStep } from "./step";

export type ActiveStep<E> = {
  node: StepNode;
  step: OnboardingStep<E>;
  presentation: Presentation;
};

export type FlowState<E> = {
  steps: readonly ActiveStep<E>[];
  index: number;
  current: ActiveStep<E> | null;
  canProceed: boolean;
  isFirst: boolean;
  isLast: boolean;
  finishing: boolean;
  labels: Required<FlowLabels>;
  /** Text for the forward button: labels.finish on the last step. */
  nextLabel: string;
  /** Forward button enabled: canProceed and not finishing. */
  nextEnabled: boolean;
  /** Back button shown: not the first step. */
  backVisible: boolean;
};

export type FlowControllerOptions<App, E> = {
  model: FlowModel;
  registry: StepKindRegistry<App, E>;
  app: App;
  /** Resolves true (or nothing) when finished; false re-enables Finish. */
  onFinish: () => Promise<boolean | void> | boolean | void;
  /** Policy for a fresh activation: given the active ids and the requested id
   * (usually the URL hash), name the step to start on. Null = default rule
   * (requested if active, else the first). */
  initialStepId?: (activeIds: string[], requested: string | null) => string | null;
  onError?: (error: unknown) => void;
};

type Instance<E> = { step: OnboardingStep<E>; mounted: Promise<void> | null };

export class FlowController<App = unknown, E = unknown> implements FlowNavigator {
  private readonly instances = new Map<string, Instance<E>>();
  private active: ActiveStep<E>[] = [];
  private index = -1;
  private finishing = false;
  private generation = 0;
  /** Ids in the order they were last shown, most recent last. */
  private shown: string[] = [];
  private readonly listeners = new Set<(state: FlowState<E>) => void>();
  private readonly enterListeners = new Set<(step: ActiveStep<E>) => void>();
  readonly labels: Required<FlowLabels>;

  constructor(private readonly options: FlowControllerOptions<App, E>) {
    const unknown = FlowModel.validateKinds(options.model, options.registry);
    if (unknown.length > 0) throw new Error(unknown.join("; "));
    this.labels = FlowModel.labels(options.model);
  }

  get model(): FlowModel {
    return this.options.model;
  }

  get currentId(): string | null {
    return this.active[this.index]?.node.id ?? null;
  }

  has(id: string): boolean {
    return this.active.some((s) => s.node.id === id);
  }

  /** Repaints on every state change. Returns an unsubscribe. */
  subscribe(listener: (state: FlowState<E>) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Called synchronously when a step becomes current (before willEnter
   * settles). The runner syncs the URL hash here. */
  onEnter(listener: (step: ActiveStep<E>) => void): () => void {
    this.enterListeners.add(listener);
    return () => this.enterListeners.delete(listener);
  }

  /** Instance for a node id, if its kind has been built. */
  instance(id: string): OnboardingStep<E> | null {
    return this.instances.get(id)?.step ?? null;
  }

  state(): FlowState<E> {
    const current = this.active[this.index] ?? null;
    const canProceed = current?.step.canProceed?.() ?? true;
    const isFirst = this.index <= 0;
    const isLast = this.index >= this.active.length - 1;
    return {
      steps: this.active,
      index: this.index,
      current,
      canProceed,
      isFirst,
      isLast,
      finishing: this.finishing,
      labels: this.labels,
      nextLabel: isLast ? this.labels.finish : this.labels.next,
      nextEnabled: current != null && canProceed && !this.finishing,
      backVisible: current != null && !isFirst,
    };
  }

  /** (Re)resolve the active steps for a context, building and mounting any
   * newly active step first. `requested` is used only on a fresh activation. */
  async setContext(ctx: FlowContext, requested: string | null = null): Promise<void> {
    const generation = ++this.generation;
    const nodes = resolveSteps(this.options.model, ctx, this.options.registry.surfacesOf);
    const instances = nodes.map((node) => this.instanceFor(node));
    for (const instance of instances) {
      instance.mounted ??= Promise.resolve(instance.step.mount?.());
      await instance.mounted;
      if (generation !== this.generation) return;
    }

    const previousId = this.currentId;
    const previousStep = this.active[this.index]?.step ?? null;
    this.active = nodes.map((node, i) => ({
      node,
      step: instances[i]!.step,
      presentation: node.presentation ??
        this.options.registry.get(node.kind).presentation ?? "step",
    }));
    this.index = -1;

    // The current step survived: keep it, no re-entry.
    if (previousId != null && this.has(previousId)) {
      this.index = this.indexOf(previousId);
      this.emit();
      return;
    }
    previousStep?.willLeave?.();

    if (this.active.length === 0) {
      this.emit();
      return;
    }

    // Returning to a side shown before: the step last shown there, as it was.
    for (let i = this.shown.length - 1; i >= 0; i--) {
      const id = this.shown[i]!;
      if (this.has(id)) {
        this.index = this.indexOf(id);
        this.emit();
        return;
      }
    }

    const ids = this.active.map((s) => s.node.id);
    const chosen = this.options.initialStepId?.(ids, requested) ??
      (requested != null && ids.includes(requested) ? requested : ids[0]!);
    await this.enter(Math.max(0, this.indexOf(chosen)));
  }

  async next(): Promise<void> {
    const current = this.active[this.index];
    if (current == null) return;
    if (!(current.step.canProceed?.() ?? true)) return;
    if (this.index >= this.active.length - 1) {
      await this.finish();
      return;
    }
    await this.enter(this.index + 1);
  }

  async prev(): Promise<void> {
    if (this.index <= 0) return;
    await this.enter(this.index - 1);
  }

  async goTo(id: string): Promise<void> {
    const index = this.indexOf(id);
    if (index < 0 || index === this.index) return;
    await this.enter(index);
  }

  async finish(): Promise<void> {
    if (this.finishing) return;
    this.finishing = true;
    this.emit();
    let ok: boolean | void;
    try {
      ok = await this.options.onFinish();
    } catch (error) {
      ok = false;
      this.options.onError?.(error);
    }
    if (ok === false) {
      this.finishing = false;
      this.emit();
    }
  }

  refresh(): void {
    this.emit();
  }

  private async enter(index: number): Promise<void> {
    const target = this.active[index];
    if (target == null) return;
    const previous = this.active[this.index];
    if (previous != null && previous !== target) previous.step.willLeave?.();

    this.index = index;
    this.shown = this.shown.filter((id) => id !== target.node.id);
    this.shown.push(target.node.id);
    this.emit();
    for (const listener of this.enterListeners) listener(target);

    try {
      await target.step.willEnter?.();
      target.step.didEnter?.();
    } catch (error) {
      this.options.onError?.(error);
    }
    this.emit();
  }

  private instanceFor(node: StepNode): Instance<E> {
    let instance = this.instances.get(node.id);
    if (instance == null) {
      const def = this.options.registry.get(node.kind);
      instance = {
        step: def.create({ node, app: this.options.app, flow: this }),
        mounted: null,
      };
      this.instances.set(node.id, instance);
    }
    return instance;
  }

  private indexOf(id: string): number {
    return this.active.findIndex((s) => s.node.id === id);
  }

  private emit(): void {
    const state = this.state();
    for (const listener of this.listeners) listener(state);
  }
}
